import React, {useEffect, useRef, useState} from "react";
import Image from "next/image";
import type {MenuProps} from 'antd';
import {Button, Drawer, Dropdown, Menu, Popover, Space} from "antd";
import {TFunction, useTranslation} from "next-i18next";
import {useUser} from "@/base/components/auth/UserProvider";
import {NextRouter, useRouter} from "next/router";
import CommentOutlined from "@ant-design/icons/CommentOutlined";
import DownOutlined from "@ant-design/icons/DownOutlined";
import HomeFilled from "@ant-design/icons/HomeFilled";
import MobileFilled from "@ant-design/icons/MobileFilled";
import StarFilled from "@ant-design/icons/StarFilled";
import {CountryFlag} from "@/base/components/CountryFlag";
import {FROM_L_DESKTOP, MAX_TABLET, useMediaQuery} from "@/base/utils/media.hook";
import Link from "next/link";
import {Url} from "next/dist/shared/lib/router/router";
import {CloseIcon} from "next/dist/client/components/react-dev-overlay/internal/icons/CloseIcon";
import {useTheme} from "@/base/components/theme/BaseThemeProvider";
import {ReactSVG} from "react-svg";
import {getThemes} from "@/base/utils/theme/theme.util";
import {DummyLink} from "@/base/components/DummyLink";
import {Money} from "@/base/utils/money";
import {DelayedEffectHook} from "@/base/types/delayed-effect.hook";
import {ResidentialFilledIcon} from "@/base/components/icon/ResidentialFilledIcon";
import {PlugFilledIcon} from "@/base/components/icon/PlugFilledIcon";
import {GuardTwoToneIcon} from "@/base/components/icon/GuardTwoToneIcon";
import {CalculatorFilledIcon} from "@/base/components/icon/CalculatorFilledIcon";
import {DollarBagFilledIcon} from "@/base/components/icon/DollarBagFilledIcon";
import {DurkaFilledIcon} from "@/base/components/icon/DurkaFilledIcon";
import {BanFilledIcon} from "@/base/components/icon/BanFilledIcon";
import {MapFilledIcon} from "@/base/components/icon/MapFilledIcon";
import {HealthFilledIcon} from "@/base/components/icon/HealthFilledIcon";
import {RSSFilledIcon} from "@/base/components/icon/RSSFilledIcon";
import {OpenBookFilledIcon} from "@/base/components/icon/OpenBookFilledIcon";
import {FlightOkFilledIcon} from "@/base/components/icon/FlightOkFilledIcon";
import {LabOkFilledIcon} from "@/base/components/icon/LabOkFilledIcon";
import {NotificationOutlinedIcon} from "@/base/components/icon/NotificationOutlinedIcon";
import {IconPieChartFilled} from "@/base/components/icon/IconPieChartFilled";
import {IconServerFilled} from "@/base/components/icon/IconServerFilled";
import {GiftFilledIcon} from "@/base/components/icon/GiftFilledIcon";
import {IconChartOutlined} from "@/base/components/icon/IconChartOutlined";
import {IconGearOutlined} from "@/base/components/icon/IconGearOutlined";
import {IconNoteOutlined} from "@/base/components/icon/IconNoteOutlined";
import {IconFolderOutlined} from "@/base/components/icon/IconFolderOutlined";
import {IconSignOutOutlined} from "@/base/components/icon/IconSignOutOutlined";
import {ServerFilledIcon} from "@/base/components/icon/ServerFilledIcon";
import {useLocaleChanger} from "@/base/components/LocaleChanger";
import dynamic from "next/dynamic";
import CodeOutlined from "@ant-design/icons/CodeOutlined";
import {ChromeOutlined, CreditCardOutlined, TrophyOutlined} from "@ant-design/icons";
import {IoExtensionPuzzle} from "react-icons/io5";
import {ImNewspaper} from "react-icons/im";

const DynamicNotificationList = dynamic(() => import('@/base/components/NotificationList'), {ssr: false});

type Props = {
	children?: React.ReactNode,
	className?: string
}

export type Item = {
	key: string;
	icon: (t: TFunction) => React.ReactNode;
	text: (t: TFunction) => string | React.ReactNode;
	description?: (t: TFunction) => string | React.ReactNode;
	tabs?: TabbedItem[];
	url?: string | null | Url;
	shadow?: boolean;
	query?: any;
	mobile: boolean;
	logged: boolean;
	type?: 'item' | 'divider';
	logout: boolean;
	hideOnMobile?: boolean;
	dashboard?: boolean;
	only_on_dashboard?: boolean;
	only_out_dashboard?: boolean;
}

export const DividerItem: Item = {
	type: 'divider',
	key: 'divider',
	icon: t => null,
	text: t => '',
	description: t => null,
	url: '',
	mobile: true,
	logged: true,
	logout: true,
	dashboard: true,
}

export const ModifyRouteUrl = (router: NextRouter, url: string | Url): Url => {
	let o: Url = typeof url === 'string' ? {
		pathname: url,
		query: {}
	} : url;

	o.query = Object.assign({}, router.query, o.query);

	return url;
}

export const isNeedScrollUrl = (router: NextRouter, url: string | Url): boolean => {
	let o: Url = typeof url === 'string' ? {
		pathname: url,
		query: {}
	} : url;

	const pathName = (o.pathname || '/').split('?')[0].split('#')[0].trim();

	if (pathName == router.pathname)
	{
		return false;
	}

	return true;
}

export type TabbedItem = {
	name: (t: TFunction) => string | React.ReactNode;
	items: Item[];
}


export const FeaturedItems: TabbedItem[] = [
	{
		name: t => t('base-common:header.items.manuals'),
		items: [
			{
				key: 'manual-dolphin',
				icon: t => <Image src="/image/header-icon-dolphin.svg" width={30} height={30} alt={t('base-common:header.items.manual-dolphin')}/>,
				text: t => t('base-common:header.items.manual-dolphin'),
				description: t => t('base-common:header.items.descriptions.manual-dolphin'),
				url: '/p/manual/dolphinanty',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'manual-ads-power',
				icon: t => <Image src="/image/header-icon-ads-power.png" width={30} height={30} alt={t('base-common:header.items.manual-ads-power')}/>,
				text: t => t('base-common:header.items.manual-ads-power'),
				description: t => t('base-common:header.items.descriptions.manual-ads-power'),
				url: '/p/manual/adspower',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'manual-undetectable',
				icon: t => <Image src="/image/header-icon-undetectable.svg" width={30} height={30} alt={t('base-common:header.items.manual-undetectable')}/>,
				text: t => t('base-common:header.items.manual-undetectable'),
				description: t => t('base-common:header.items.descriptions.manual-undetectable'),
				url: '/p/manual/undetectable',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	},
	{
		name: t => t('base-common:header.items.blog'),
		items: [
			{
				key: 'blog-residential',
				icon: t => <ResidentialFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.blog-residential'),
				description: t => t('base-common:header.items.descriptions.blog-residential'),
				url: '/p/blog/residential-proxy',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'blog-http-or-socks',
				icon: t => <PlugFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.blog-http-or-socks'),
				description: t => t('base-common:header.items.descriptions.blog-http-or-socks'),
				url: '/p/blog/https-vs-socks5',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'blog-hide-ip',
				icon: t => <GuardTwoToneIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.blog-hide-ip'),
				description: t => t('base-common:header.items.descriptions.blog-hide-ip'),
				url: '/p/blog/how-to-hide-your-ip-address',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	},
	{
		name: t => t('base-common:header.features'),
		items: [
			{
				key: 'feature-calculator',
				icon: t => <CalculatorFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.blog-feature-calculator'),
				description: t => t('base-common:header.items.descriptions.blog-feature-calculator'),
				url: '/?scroll=buy',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'feature-referral',
				icon: t => <DollarBagFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.blog-feature-referral'),
				description: t => t('base-common:header.items.descriptions.blog-feature-referral'),
				url: '/dashboard/referral',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'feature-support',
				icon: t => <DurkaFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.blog-feature-support'),
				description: t => t('base-common:header.items.descriptions.blog-feature-support'),
				url: '/support',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	}
];
export const ProductsItems: TabbedItem[] = [
	{
		name: t => t('base-common:header.items.products-datacenter'),
		items: [
			{
				key: 'proxies-private',
				icon: t => <ServerFilledIcon width={30} height={30} className={`!text-amber-400`}/>,
				text: t => <span className={`flex gap-2 items-center w-full`}>
					{t('base-common:header.items.products-private')}
					<StarFilled className={`!text-amber-400`}/>
					<span className={`bg-amber-500/10 px-1.5 rounded-md py-0.5 ms-auto font-semibold font-montserrat text-text-2 text-sm tracking-wider whitespace-nowrap`}>0,9 $ / IP</span>
				</span>,
				description: t => t('base-common:header.items.descriptions.products-private'),
				url: '/?type=private&scroll=buy',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'proxies-shared',
				icon: t => <ServerFilledIcon width={30} height={30}/>,
				text: t => <span className={`flex gap-2 items-center w-full`}>
					{t('base-common:header.items.products-shared')}
					<span className={`bg-primary/10 px-1.5 rounded-md py-0.5 ms-auto font-semibold font-montserrat text-text-2 text-sm tracking-wider whitespace-nowrap`}>0,08 $ / IP</span>
				</span>,
				description: t => t('base-common:header.items.descriptions.products-shared'),
				url: '/?type=shared&scroll=buy',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	},
	{
		name: t => t('base-common:header.items.products-residential'),
		items: [
			{
				key: 'proxies-residential-gb',
				icon: t => <HomeFilled className={`text-3xl !text-violet-500`}/>,
				text: t => <span className={`flex items-center w-full`}>{t('base-common:header.items.products-residential-gb')}
					<span className={`bg-violet-500/10 px-1.5 rounded-md py-0.5 ms-auto font-semibold font-montserrat text-text-2 text-sm tracking-wider whitespace-nowrap`}>1.50 $ / GB</span>
				</span>,
				description: t => t('base-common:header.items.descriptions.products-residential-gb'),
				url: '/proxies/residential?type=residential_gb',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'proxies-residential-static-gb',
				icon: t => <HomeFilled className={`text-3xl !text-violet-500`}/>,
				text: t => <span className={`flex gap-2 items-center`}>
					{t('base-common:header.items.products-residential-static-gb')}
					<StarFilled className={`!text-violet-500`}/>
					<span className={`bg-violet-500/10 px-1.5 rounded-md py-0.5 ms-auto font-semibold font-montserrat text-text-2 text-sm tracking-wider whitespace-nowrap`}>2 $ / IP</span>
				</span>,
				description: t => t('base-common:header.items.descriptions.products-residential-static-gb'),
				url: '/proxies/residential?type=residential_static_gb',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	},
	{
		name: t => t('base-common:header.items.products-mobile'),
		items: [
			{
				key: 'proxies-mobile-gb',
				icon: t => <MobileFilled className={`text-3xl !text-emerald-450`}/>,
				text: t => <span className={`flex gap-2 items-center`}>{t('base-common:header.items.products-mobile-gb')}
					<span className={`bg-emerald-450/10 px-1.5 rounded-md py-0.5 ms-auto font-semibold font-montserrat text-text-2 text-sm tracking-wider whitespace-nowrap`}>0,85 $ / GB</span>
				</span>,
				description: t => t('base-common:header.items.descriptions.products-mobile-gb'),
				url: '/proxies/mobile?type=mobile_gb',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'proxies-mobile-static-gb',
				icon: t => <MobileFilled className={`text-3xl !text-emerald-450`}/>,
				text: t => <span className={`flex gap-2 items-center`}>{t('base-common:header.items.products-mobile-static-gb')}
					<span className={`bg-emerald-450/10 px-1.5 rounded-md py-0.5 ms-auto font-semibold font-montserrat text-text-2 text-sm tracking-wider whitespace-nowrap`}>3 $ / IP</span>
				</span>,
				description: t => t('base-common:header.items.descriptions.products-mobile-static-gb'),
				url: '/proxies/mobile?type=mobile_static_gb',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	},
	{
		name: t => t('base-common:header.items.software'),
		items: [
			{
				key: 'extension',
				icon: t => <IoExtensionPuzzle className={`text-3xl !text-pink-400`}/>,
				text: t => <span className={`flex gap-2 items-center`}>{t('base-common:header.items.extension')}
					<span className={`bg-pink-400/10 px-1.5 rounded-md py-0.5 ms-auto font-semibold font-montserrat text-text-2 text-sm tracking-wider whitespace-nowrap`}>
						<ChromeOutlined/>
					</span>
				</span>,
				description: t => t('base-common:header.items.descriptions.extension'),
				url: '/extension',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	},
];

export const ResourceItems: TabbedItem[] = [
	{
		name: t => t('base-common:header.items.to-read'),
		items: [
			{
				key: 'news',
				icon: t => <ImNewspaper className={`text-3xl !text-primary`}/>,
				text: t => <span className={`flex items-center w-full`}>{t('base-common:header.items.news')}
					<span className={`bg-primary/10 px-1.5 rounded-md py-0.5 ms-auto font-semibold font-montserrat text-text-2 text-sm tracking-wider whitespace-nowrap`}>NEW</span>
				</span>,
				description: t => t('base-common:header.items.descriptions.news'),
				url: '/news',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'manual',
				icon: t => <OpenBookFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.manuals'),
				description: t => t('base-common:header.items.descriptions.manuals'),
				url: '/manual',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'blog',
				icon: t => <RSSFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.blog'),
				description: t => t('base-common:header.items.descriptions.blog'),
				url: '/blog',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'apidocs',
				icon: t => <CodeOutlined className={`text-3xl !text-primary`}/>,
				text: t => t('base-common:header.items.api'),
				description: t => t('base-common:header.items.descriptions.api'),
				url: '/apidocs',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	},
	{
		name: t => t('base-common:header.items.checkers'),
		items: [
			{
				key: 'ip-checker',
				icon: t => <LabOkFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.checker-ip'),
				description: t => t('base-common:header.items.descriptions.checker-ip'),
				url: '/checker/ip',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'proxy-checker',
				icon: t => <FlightOkFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.checker-proxy'),
				description: t => t('base-common:header.items.descriptions.checker-proxy'),
				url: '/checker/proxy',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	},
	{
		name: t => t('base-common:header.items.other'),
		items: [
			{
				key: 'countries',
				icon: t => <MapFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.countries'),
				description: t => t('base-common:header.items.descriptions.countries'),
				url: '/countries',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'blacklist',
				icon: t => <BanFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.blacklist'),
				description: t => t('base-common:header.items.descriptions.blacklist'),
				url: '/blacklist',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
			{
				key: 'status',
				icon: t => <HealthFilledIcon width={30} height={30}/>,
				text: t => t('base-common:header.items.status'),
				description: t => t('base-common:header.items.descriptions.status'),
				url: '/status',
				mobile: false,
				logged: false,
				logout: false,
				dashboard: false,
			} as Item,
		]
	}
]

export const ResourceItem = ({item, onClick}: { item: Item, onClick?: () => void }) => {
	const {t} = useTranslation();
	const router = useRouter();

	return <Link href={ModifyRouteUrl(router, item.url || router.pathname)} scroll={isNeedScrollUrl(router, item.url || router.pathname)} className={`hover:[&>div]:bg-primary/10 flex group max-w-[363px]`} onClick={onClick}>
		<div className={`flex gap-4 w-full p-2.5 rounded-md duration-150`}>
			<div className={`bg-primary-100 h-fit p-2.5 rounded-md flex items-center w-fit text-color group-hover:!bg-main-bg group-hover:text-primary duration-150`}>
				{/*<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" height={20} width={20}>*/}
				{/*	<path*/}
				{/*		fill="currentColor"*/}
				{/*		d="M96 0C43 0 0 43 0 96V416c0 53 43 96 96 96H384h32c17.7 0 32-14.3 32-32s-14.3-32-32-32V384c17.7 0 32-14.3 32-32V32c0-17.7-14.3-32-32-32H384 96zm0 384H352v64H96c-17.7 0-32-14.3-32-32s14.3-32 32-32zm32-240c0-8.8 7.2-16 16-16H336c8.8 0 16 7.2 16 16s-7.2 16-16 16H144c-8.8 0-16-7.2-16-16zm16 48H336c8.8 0 16 7.2 16 16s-7.2 16-16 16H144c-8.8 0-16-7.2-16-16s7.2-16 16-16z"/>*/}
				{/*</svg>*/}
				{item.icon && item.icon(t)}
			</div>
			<p className={`flex flex-col gap-1 justify-center`}>
				{item?.text && <span className={`font-semibold text-text-2 text-md`}>
					{item?.text(t) || ''}
				</span>}
				{item.description && <span className={`text-color text-sm`}>
					{item.description(t) || ''}
				</span>}
			</p>
		</div>
	</Link>
}

export const HeaderItem = ({item}: { item: Item }) => {
	const {t} = useTranslation();
	const router = useRouter();
	const [isHovered, setIsHovered] = useState(false);

	return <Link
		key={item.key}
		href={ModifyRouteUrl(router, item.url || router.pathname)}
		scroll={isNeedScrollUrl(router, item.url || router.pathname)}
		className={`text-inherit font-bold ${(getActiveKey(router.route) == item.key || isHovered) && 'text-primary'}  max-lg:!text-sm`}
		shallow={item?.shadow || false}
	>
		<HeaderOneItem item={item}/>
	</Link>
}

export const PopoverTab = ({items, className = '', children, onSelect = (item) => null}: { items: TabbedItem[], className?: string, children?: React.ReactNode, onSelect?: (item: Item) => void }) => {
	const {t} = useTranslation();

	return <div className={`${className}`}>
		<div className={`flex flex-wrap`}>
			{items.map((item, index) => <div key={index} className={`border-b border-primary-100 flex flex-col gap-2.5`}>
				<div className={`font-semibold text-md text-primary p-2.5`}>
					{item.name(t)}
				</div>
				{item.items.map((item, index) => <ResourceItem key={index} item={item} onClick={() => onSelect(item)}/>)}
			</div>)}
			{children}
		</div>
	</div>
}

export const HeaderOneItem = ({item, onClick}: { item: Item, onClick?: () => void }) => {
	const {t} = useTranslation();
	const [isOpened, setIsOpened] = useState(false);
	const [itemSelected, setItemSelected] = useState<boolean>(false);
	const [overSelected, setOverSelected] = useState<boolean>(false);

	const tabs = item.tabs || [];
	const tabbedItems = tabs && tabs.length > 0 && <PopoverTab items={tabs} className={`w-fit`} onSelect={item => {
		setIsOpened(false);
		onClick && onClick();
	}}/>;

	const IS_MOBILE = useMediaQuery(MAX_TABLET);

	DelayedEffectHook(() => {
		if (!IS_MOBILE)
		{
			setIsOpened(itemSelected || overSelected);
		}
	}, [itemSelected, overSelected], 250);

	return <>
		{tabbedItems && <Drawer
			open={isOpened}
			placement={IS_MOBILE ? 'right' : `top`}
			zIndex={IS_MOBILE ? 9999999 : 500}
			onClose={() => setIsOpened(false)}
			closeIcon={IS_MOBILE && <CloseIcon/>}
			className={`w-full`}
			maskClassName={`lg:!bg-transparent`}
			classNames={{
				body: `lg:!mt-[74px] shadow shadow-lg !overflow-auto lg:!p-0`,
				content: `flex !overflow-inherit`,
			}}
			height={'auto'}
			// forceRender={true}
			onMouseEnter={() => {
				setOverSelected(true);
				setIsOpened(true);
			}}
			onMouseLeave={() => {
				setOverSelected(false);
			}}
		>
			<div className={`max-w-[1280px] mx-auto py-4`}>
				{tabbedItems}
			</div>
		</Drawer>}
		<span
			onClick={() => {
				if (tabs.length > 0)
				{
					if (IS_MOBILE)
					{
						setIsOpened(!isOpened);
					}
					else
					{
						setItemSelected(true);
					}
				}
				else
				{
					onClick && onClick();
				}
			}}
			onMouseEnter={() => {
				if (tabs.length > 0)
				{
					setItemSelected(true);
				}
			}}
			onMouseLeave={() => {
				if (tabs.length > 0)
				{
					setItemSelected(false);
				}
			}}
		>{(item?.text(t) || '')}</span>
	</>;
}

export const Items: Item[] = [
	{
		key: 'main',
		icon: t => <IconServerFilled width={30} height={30}/>,
		text: t => t('base-common:header.items.main'),
		url: '/',
		mobile: false,
		logged: false,
		logout: false,
		dashboard: false,
		only_on_dashboard: true,
	} as Item,
	{
		key: 'dashboard',
		icon: t => <IconPieChartFilled width={30} height={30}/>,
		text: t => t('base-common:header.items.dashboard'),
		url: '/dashboard',
		mobile: false,
		logged: true,
		logout: false,
		dashboard: false,
		hideOnMobile: true,
		only_out_dashboard: true,
	} as Item,
	{
		key: 'dashboard',
		icon: t => <IconPieChartFilled width={30} height={30}/>,
		text: t => t('base-common:header.items.dashboard'),
		url: '/dashboard',
		mobile: false,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	{
		...DividerItem,
		key: 'divider-d-1',
		mobile: true,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	{
		key: 'promo',
		icon: t => <TrophyOutlined className={`!text-primary !text-[30px]`}/>,
		text: t => <>
			{t('base-common:header.items.promo')}
			<span className={`bg-primary/30 px-1.5 ms-2 rounded-md py-0.5 font-semibold font-montserrat text-text-2 text-sm tracking-wider whitespace-nowrap`}>V2</span>
		</>,
		url: '/dashboard/promo',
		mobile: true,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	// {
	// 	key: 'api',
	// 	icon: t => <Image src="/image/header-icon-5.svg" width={30} height={30} alt={t('base-common:header.items.api')}/>,
	// 	text: t => t('base-common:header.items.api'),
	// 	url: '/api',
	// 	mobile: false,
	// 	logged: false,
	// 	logout: false,
	// 	dashboard: false,
	// } as Item,
	{
		key: 'referral',
		icon: t => <GiftFilledIcon width={30} height={30}/>,
		text: t => t('base-common:header.items.referral'),
		url: '/dashboard/referral',
		mobile: true,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	{
		...DividerItem,
		key: 'divider-d-2',
		mobile: true,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	{
		key: 'topup',
		icon: t => <CreditCardOutlined className={`!text-primary !text-[30px]`}/>,
		text: t => t('base-common:header.items.topup'),
		url: '/dashboard/topup',
		mobile: true,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	{
		key: 'apikeys',
		icon: t => <IconChartOutlined width={30} height={30}/>,
		text: t => t('base-common:header.items.api-keys'),
		url: '/dashboard/api',
		mobile: false,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	{
		key: 'settings',
		icon: t => <IconGearOutlined width={30} height={30}/>,
		text: t => t('base-common:header.items.settings'),
		url: '/dashboard/settings',
		mobile: true,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	{
		key: 'reviews',
		icon: t => <CommentOutlined className={`!text-primary !text-[27px]`}/>,
		text: t => t('base-common:header.items.reviews'),
		url: '/reviews',
		mobile: false,
		logged: false,
		logout: false,
		dashboard: false,
	} as Item,
	{
		key: 'faq',
		icon: t => <IconNoteOutlined width={30} height={30}/>,
		text: t => t('base-common:header.items.faq'),
		url: '/faq',
		mobile: false,
		logged: false,
		logout: false,
		dashboard: false,
	} as Item,
	{
		key: 'products',
		icon: t => <IconFolderOutlined width={30} height={30}/>,
		text: t => <>{t('base-common:header.items.products')}
			<svg xmlns="http://www.w3.org/2000/svg" width={23} height={14} viewBox="0 0 512 512" className={`pt-1`}>
				<path fill={`currentColor`} d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/>
			</svg>
		</>,
		// content: () => <PopoverTab items={ResourceItems} className={`w-full`}/>,
		tabs: ProductsItems,
		url: null,
		mobile: false,
		logged: false,
		logout: false,
		dashboard: false,
	} as Item,
	{
		key: 'resources',
		icon: t => <IconFolderOutlined width={30} height={30}/>,
		text: t => <>{t('base-common:header.items.featured')}
			<svg xmlns="http://www.w3.org/2000/svg" width={23} height={14} viewBox="0 0 512 512" className={`pt-1`}>
				<path fill={`currentColor`} d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/>
			</svg>
		</>,
		// content: () => <PopoverTab items={ResourceItems} className={`w-full`}/>,
		tabs: FeaturedItems,
		url: null,
		mobile: false,
		logged: false,
		logout: false,
		dashboard: false,
	} as Item,
	{
		key: 'resources2',
		icon: t => <IconFolderOutlined width={30} height={30}/>,
		text: t => <>{t('base-common:header.items.resources')}
			<svg xmlns="http://www.w3.org/2000/svg" width={23} height={14} viewBox="0 0 512 512" className={`pt-1`}>
				<path fill={`currentColor`} d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/>
			</svg>
		</>,
		// content: () => <PopoverTab items={ResourceItems} className={`w-full`}/>,
		tabs: ResourceItems,
		url: null,
		mobile: false,
		logged: false,
		logout: false,
		dashboard: false,
	} as Item,
	{
		...DividerItem,
		key: 'divider-d-3',
		mobile: true,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	{
		key: 'signout',
		icon: t => <IconSignOutOutlined width={30} height={30}/>,
		text: t => t('base-common:header.items.signout'),
		url: {
			query: {
				signout: true
			}
		},
		shadow: true,
		mobile: false,
		logged: true,
		logout: false,
		dashboard: true,
	} as Item,
	...(process.env.NEXT_PUBLIC_OLD_SITE_URL ? [
		{
			key: 'old_site',
			icon: t => <Image src="/image/logo-old.png" width={30} height={30} alt={t('base-common:header.items.old-site')}/>,
			text: t => t('base-common:header.items.old-site'),
			url: process.env.NEXT_PUBLIC_OLD_SITE_URL,
			shadow: true,
			mobile: false,
			logged: true,
			logout: false,
			dashboard: true,
		} as Item
	] : []),
	{
		key: 'auth',
		icon: t => <IconSignOutOutlined width={30} height={30}/>,
		text: t => t('base-common:header.items.auth'),
		url: '/auth',
		mobile: false,
		logged: false,
		logout: true,
		dashboard: false,
	} as Item
]

type LanguageButton = {
	key: string;
	countryCode: string;
	visibleName: (t: TFunction) => string;
	visibleShortName: (t: TFunction) => string;
}

export const getActiveKey = (route: string): string => {
	const item = Items.find(item => item.url === route);
	if (item)
	{
		return item.key;
	}

	if (route.startsWith('/dashboard'))
	{
		return 'dashboard';
	}

	return '';
}

export const Header = React.forwardRef((props: Props, ref: React.ForwardedRef<HTMLDivElement>) => {
	const [open, setOpen] = useState(false);

	const Languages: LanguageButton[] = [];

	// foreach NEXT_PUBLIC_APP_LOCALES.toLowerCase().split(',') as $locale
	if (process.env.NEXT_PUBLIC_APP_LOCALES && process.env.NEXT_PUBLIC_APP_LOCALES.length > 0)
	{
		Languages.splice(0, Languages.length);
		const locales = process.env.NEXT_PUBLIC_APP_LOCALES.toString().toLowerCase().split(',');
		locales.forEach(locale => {
			const country = locale == 'en' ? 'gb' : (locale == 'uk' ? 'ua' : locale);
			Languages.push({
				key: locale,
				countryCode: country,
				visibleName: t => t(`base-common:languages.${locale}`),
				visibleShortName: t => t(`base-common:sort-languages.${locale}`),
			})
		})
	}

	if (Languages.length == 0)
	{
		Languages.push(...[
			{
				key: 'en',
				countryCode: 'gb',
				visibleName: t => t('base-common:languages.en'),
				visibleShortName: t => 'Eng',
			} as LanguageButton,
			{
				key: 'uk',
				countryCode: 'ua',
				visibleName: t => t('base-common:languages.uk'),
				visibleShortName: t => 'Ukr',
			} as LanguageButton,
			{
				key: 'ru',
				countryCode: 'ru',
				visibleName: t => t('base-common:languages.ru'),
				visibleShortName: t => 'Rus',
			} as LanguageButton,
		])
	}

	const {t, i18n} = useTranslation('common');
	const UserContext = useUser();
	// router
	const router = useRouter();

	const showDrawer = () => {
		setOpen(true);
	};

	const onClose = () => {
		setOpen(false);
	};

	// easter egg on five time press logo
	const [pressLogo, setPressLogo] = useState(0);
	const [pressLogoTimeout, setPressLogoTimeout] = useState<any>(null);

	const [isSudo, setIsSudo] = useState(false);
	useEffect(() => {
		UserContext.isSudo().then(setIsSudo);
	}, [UserContext.user]);

	type MenuItem = Required<MenuProps>['items'][number];

	const isMobile = useMediaQuery(`(max-width: 1024px)`)
	const isPc = useMediaQuery(FROM_L_DESKTOP)

	function getItem(
		label: React.ReactNode,
		key: React.Key,
		icon?: React.ReactNode,
		size?: 'large' | 'small' | 'default',
	): MenuItem
	{
		return {
			key,
			icon,
			label,
			size,
		} as MenuItem;
	}

	const [items, setItemsState] = useState<MenuItem[]>([])

	useEffect(() => {
		const newItems: MenuItem[] = [];
		Items.forEach(item => {
			if ((UserContext.loggedIn && item.logout) || (!UserContext.loggedIn && item.logged) || (item.hideOnMobile))
			{
				return;
			}

			const text = <HeaderOneItem item={item} onClick={() => {
				onClose();
			}}/>;

			newItems.push(getItem(text, item.key, item.icon(t)));
		})

		setItemsState(newItems);
	}, [UserContext.loggedIn, router.locale]);

	// locale state
	const [localeItems, setLocaleItems] = useState<MenuProps['items']>([]);

	useEffect(() => {
		const newItems: MenuItem[] = [];
		Languages.forEach(item => {
			newItems.push(getItem(item.visibleName(t), item.key, <div className="pr-2 flex items-center my-1.5"><CountryFlag code={item.countryCode} height={25}/></div>, "large"));
		})

		setLocaleItems(newItems);
	}, [router.locale])

	const [openNotifications, setOpenNotifications] = useState(false);

	const {theme, setTheme} = useTheme();
	const themes = getThemes();
	const toggleTheme = () => {
		const nextTheme = themes.filter(d => d !== theme)[0];
		setTheme(nextTheme);
	}

	const {setLocale} = useLocaleChanger();

	const isInDashboard = router.route?.toLowerCase()?.includes('/dashboard');

	// make key listener listen for HESOYAM
	const cheatLine = useRef('');
	useEffect(() => {
		const hFunc = (c: string) => {
			let res = 0;
			for (let i = 0; i < c.length; i++)
			{
				res += c.toLowerCase().charCodeAt(i) * (i + 1) + c.length
			}
			return res;
		}

		const calls = {
			'3094': () => { // hesoyam
				// todo: call hide achivement
				alert('HESOYAM');
			},
			'3016': () => { // meisgod
				setPressLogo(10);
			},
			'2977': () => { // allfree
				// set sp_force_amount
				// @ts-ignore
				window.sp_force_amount = 0;
			},
			'3048': () => { // allflip
				// add * style which rotate all text
				const styleBlock = document.createElement('style');
				// make animation
				styleBlock.innerHTML = `
					.ant-layout-content * {
						animation: flip 100s infinite;
					}
					@keyframes flip {
						0% {
							transform: rotate(0deg);
						}
						100% {
							transform: rotate(360deg);
						}
					}
				`;
				document.head.appendChild(styleBlock);
			},
			'2945': () => { // skibidi
				// add skibidi thinkg to webpage
				// find all img and replace src to https://www.google.com/search?q=skibidi+toilet+gif&num=10&newwindow=1&sca_esv=1e68560f6949762e&udm=2&biw=1175&bih=651&sxsrf=AHTn8zpQ493JwQJsV7CqvOTCw5_glInW0Q%3A1741565515547&ei=Sy7OZ8CSIdu2wPAP7eLY8Qc&ved=0ahUKEwiA5KCPnf6LAxVbGxAIHW0xNn4Q4dUDCBE&uact=5&oq=skibidi+toilet+gif&gs_lp=EgNpbWciEnNraWJpZGkgdG9pbGV0IGdpZjIFEAAYgAQyBRAAGIAEMgUQABiABDIFEAAYgAQyBRAAGIAEMgUQABiABDIFEAAYgAQyBRAAGIAEMgUQABiABDIFEAAYgARI7wpQwwhYoApwA3gAkAEAmAFKoAHQAaoBATO4AQPIAQD4AQGYAgagAuoBwgIKEAAYgAQYQxiKBcICBhAAGAcYHsICCBAAGAcYChgemAMAiAYBkgcBNqAH_A8&sclient=img#vhid=zMHNMKnrR9DuyM&vssid=mosaic:~:text=Tenor-,165%20%C3%97%20293,-Skibidi%20Toilet%20GIF
				const imgs = document.querySelectorAll('img');
				imgs.forEach(img => {
					img.src = 'https://media.tenor.com/YzseE_-j48QAAAAM/skibidi-toilet.gif';
					// remove srcset attr
					img.removeAttribute('srcset');
				});
				// all svg els
				const svgs = document.querySelectorAll('svg');
				svgs.forEach(svg => {
					// add this https://media.tenor.com/YzseE_-j48QAAAAM/skibidi-toilet.gif
					svg.innerHTML = `<image href="https://media.tenor.com/YzseE_-j48QAAAAM/skibidi-toilet.gif"/>`;
				});

			}
		}

		const listener = (e: KeyboardEvent) => {
			if (e.key.length > 1)
			{
				return;
			}
			
			cheatLine.current += e.key;

			// limit is 16 chars
			if (cheatLine.current.length > 7)
			{
				cheatLine.current = cheatLine.current.substring(cheatLine.current.length - 7);
			}
			const h = hFunc(cheatLine.current);
			console.debug('H:', h, cheatLine.current);
			// @ts-ignore
			if (calls[h])
			{
				// @ts-ignore
				calls[h]();
			}
		}

		window.addEventListener('keydown', listener);

		return () => {
			window.removeEventListener('keydown', listener);
		}
	}, []);

	const AuthItem = Items.find(item => item.key === 'auth') as Item;
	const HeaderPc = !isMobile && <div className="space-x-6 flex-grow flex justify-start h-full items-center">
		{Items
			.filter(item => !item.mobile && !item.dashboard)
			.filter(item => item != AuthItem)
			.filter(item => !isInDashboard && item.only_out_dashboard || isInDashboard && item.only_on_dashboard || !item.only_on_dashboard && !item.only_out_dashboard)
			.filter(item => (!item.logged && !item.logout) || (item.logged && UserContext.loggedIn) || (item.logout && !UserContext.loggedIn))
			.map((item, key) => <HeaderItem key={key} item={item}/>)}
	</div>;

	return (
		<div className={`${props.className || ''} flex ${pressLogo >= 4 && `!bg-amber-500 !text-white`}`} ref={ref}>
			<div className="flex flex-wrap space-x-3 py-3 max-md:pl-5 px-5 max-lg:pl-0 items-center mr-auto w-full mx-auto max-w-screen-2xl relative xl:px-10 2xl:px-4 min-h-[66px]">
				<a href={`/`} className={`${router.asPath == '/' && 'sp-logo-hide'} mr-auto max-md:inline`} onClick={(e) => {
					e.preventDefault();

					router.push('/');
				}} aria-label={`${process.env.NEXT_PUBLIC_APP_NAME} ${t('base-common:header.items.main')}`}>
					<ReactSVG src="/image/logo.svg" className={`${pressLogo >= 4 && `scale-150`} [&_*]:!text-primary`}/>
				</a>
				{isPc && <div className={`max-w-[1280px] w-full absolute top-0 left-1/2 -translate-x-1/2 h-full`}>
					{HeaderPc}
				</div>}
				{pressLogo >= 4 && <p className={`ms-10 text-2xl`}>`GOD MODE`</p>}
				{pressLogo >= 4 && <p className={`ms-10 text-2xl`}>Original 1n1 project</p>}
				{!isPc && !isMobile && HeaderPc}
				{isSudo && <div className={`relative z-50`}>
					<p>Sudo</p>
					<DummyLink href={`#`} onClick={UserContext.exitFromSudo} className="text-primary">Exit sudo</DummyLink>
				</div>}
				{isMobile ? (
					<div>
						<Image src={"/image/header-menu.svg"} alt={"Menu"} width={35} height={35} onClick={showDrawer}/>
						<Drawer placement="right" open={open} onClose={onClose} closeIcon={null} className="!bg-primary-50 bg-none relative">
							<Button
								className={`!ms-auto !absolute top-5 right-5`}
								onClick={onClose}
							>
								<CloseIcon/>
							</Button>
							{UserContext.loggedIn ? (
								<div className="flex flex-wrap space-x-5 mb-4">
									<Image src={UserContext.user?.avatar_url || '/image/logo.svg'} alt={"Avatar"} width={60} height={60} className="rounded-xl"/>
									<div className="flex flex-col my-auto space-y-2">
										<p className="font-semibold">{UserContext.user?.name || 'User'}</p>
										<p>{t('base-common:words.balance')} <span className="font-semibold">{Money.format(UserContext.user?.balance?.balance || '0', UserContext.currency, router.locale)}</span></p>
									</div>
								</div>
							) : null}
							<div className="ml-7 mb-3 flex">
								<Dropdown
									className={`!cursor-pointer`}
									menu={{
										items: localeItems,
										selectable: true,
										selectedKeys: [i18n.language],
										onClick: async ({key}) => {
											return setLocale(key)
										}
									}}

								>
									<Space>
										<div className="pr-2 flex items-center my-1.5">
											<CountryFlag code={Languages.find(d => d.key == i18n.language)?.countryCode || "ua"} height={25} width={25} className="!rounded-full"/>
										</div>
										<p className="font-bold">{Languages.find(d => d.key == i18n.language)?.visibleShortName(t) || "Ukr"}</p>
										<DownOutlined/>
									</Space>
								</Dropdown>
								<div className="flex flex-wrap ml-auto space-x-3">
									{themes.length > 1 && <Button className="!h-auto !p-2 !rounded-xl !flex" onClick={toggleTheme}>
										<Image src={"/base/image/icon-bulb.svg"} width={30} height={30} alt={'Lightbulb'}/>
									</Button>}
									{UserContext.loggedIn && <Popover content={openNotifications && <DynamicNotificationList className={`max-h-[300px] overflow-y-auto w-full min-w-[250px] md:max-w-[280px]`}/>} title={t('base-common:header.items.notifications')} trigger="click" placement={'bottom'}
									                                  rootClassName={`max-sm:!pr-5 w-[95%]`} onOpenChange={setOpenNotifications} open={openNotifications}>
										<Button className="!h-auto !p-2 !rounded-xl !flex">
											<NotificationOutlinedIcon width={30} height={30}/>
										</Button>
									</Popover>}
									{/*<Button className="!h-auto !p-2 !rounded-xl !flex">*/}
									{/*	<Image src={"/base/image/icon-cart.svg"} width={30} height={30} alt={"Notification"}/>*/}
									{/*</Button>*/}
								</div>
							</div>
							<Menu
								// onClick={onClick}
								defaultSelectedKeys={[getActiveKey(router.route)]}
								mode="inline"
								rootClassName={`[&_.ant-menu-item-only-child]:!h-[10px]`}
								items={items}
								className="w-full"
								onClick={({key}) => {
									const item = Items.find(item => item.key === key);
									if (item?.url)
									{
										router.push(ModifyRouteUrl(router, item?.url || router.pathname), undefined, {
											shallow: item?.shadow || false,
											scroll: isNeedScrollUrl(router, item.url || router.pathname)
										})
										onClose()
									}
								}}
							/>
						</Drawer>
					</div>
				) : (
					<div className="flex space-x-5 ml-7 z-[2]">
						<div className="flex flex-wrap space-x-4 items-center">
							{UserContext.loggedIn && <Popover content={openNotifications && <DynamicNotificationList className={`max-h-[300px] overflow-y-auto w-full min-w-[250px] md:max-w-[280px]`}/>} title={t('base-common:header.items.notifications')} trigger="click" placement={'bottom'}
							                                  onOpenChange={setOpenNotifications} open={openNotifications}>
								<Button className="!h-auto !p-3 !rounded-xl btn-primary-50 !flex !shadow-none">
									<NotificationOutlinedIcon width={23} height={23}/>
								</Button>
							</Popover>}
							{themes.length > 1 && <Button className="!h-auto !p-3 !rounded-xl btn-primary-50 !flex !shadow-none" onClick={toggleTheme}>
								<Image src={"/base/image/icon-bulb.svg"} width={23} height={23} alt={"Notification"}/>
							</Button>}
							<div>
								<Dropdown
									menu={{
										items: localeItems,
										selectable: true,
										selectedKeys: [i18n.language],
										onClick: async ({key}) => {
											return setLocale(key)
										}
									}}
									className={`!cursor-pointer`}
								>
									<Space>
										<div className="pr-2 flex items-center my-1.5">
											<CountryFlag code={Languages.find(d => d.key == i18n.language)?.countryCode || "ua"} height={25} width={25} className="!rounded-full"/>
										</div>
										<p className="font-bold hidden lg:inline max-xl:!hidden">{Languages.find(d => d.key == i18n.language)?.visibleShortName(t) || "Ukr"} ({i18n.language?.toUpperCase() || "UA"})</p>
										<DownOutlined/>
									</Space>
								</Dropdown>
							</div>
							{UserContext.loggedIn ? (
								<Dropdown
									className={`!cursor-pointer`}
									dropdownRender={menu => {
										return <div>
											<div className="flex flex-col my-auto space-y-2 block lg:hidden bg-main-bg mb-2 rounded-xl p-4 shadow-2xl ">
												<p className="font-medium">{UserContext.user?.name || 'User'}</p>
												<p><span className="font-semibold">{Money.format(UserContext.user?.balance?.balance || '0', UserContext.currency, router.locale)}</span></p>
											</div>
											{menu}
										</div>
									}}
									menu={{
										items: Items
											.filter(item => item.dashboard)
											.map(item => ({
												label: item?.text(t),
												key: item.key,
												icon: <div className="py-1 flex">{item.icon(t)}</div>,
												size: "large",
												type: item?.type
											} as MenuItem)),
										selectable: true,
										selectedKeys: [getActiveKey(router.route)],
										onClick: async ({key}) => {
											const item = Items.find(item => item.key === key);
											router.push(ModifyRouteUrl(router, item?.url || router.pathname), undefined, {
												shallow: item?.shadow || false,
												scroll: isNeedScrollUrl(router, item?.url || router.pathname)
											})
										}
									}}
								>
									<div className="flex flex-wrap space-x-5 pl-3">
										<Image src={UserContext.user?.avatar_url || '/image/logo.svg'} alt={"Avatar"} width={50} height={50} className="rounded-xl"/>
										<div className="flex flex-col my-auto space-y-2 max-xl:!hidden lg:inline">
											<p className="font-medium">{UserContext.user?.name || 'User'}</p>
											<p><span className="font-semibold">{Money.format(UserContext.user?.balance?.balance || '0', UserContext.currency, router.locale)}</span></p>
										</div>
										<DownOutlined/>
									</div>
								</Dropdown>
							) : <Link
								key={AuthItem.key}
								href={ModifyRouteUrl(router, AuthItem.url || router.pathname)}
								scroll={isNeedScrollUrl(router, AuthItem.url || router.pathname)}
								className={`text-inherit font-bold ${getActiveKey(router.route) == AuthItem.key && 'text-primary'}  max-lg:!text-sm`}
								shallow={AuthItem?.shadow || false}
							>
								{AuthItem?.text(t) || ''}
							</Link>}
						</div>
					</div>
				)}
			</div>
		</div>
	)
})